import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
// Icons
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { apiErrorMessage } from "../../utils/apiError";
import EditIcon from "@mui/icons-material/EditRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLessRounded";
import StorefrontIcon from "@mui/icons-material/StorefrontRounded";
import LayersIcon from "@mui/icons-material/LayersRounded";
import ViewModuleIcon from "@mui/icons-material/ViewModuleRounded";
import AddIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import RouterIcon from "@mui/icons-material/RouterRounded";
import PinDropRoundedIcon from "@mui/icons-material/PinDropRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";

// ─── Blue/White Design Tokens (shared with the Pawn reports) ────────────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const BG_ALT = "#F1F5F9";
const SUCCESS = "#059669";
const DANGER = "#DC2626"; // mandatory-field violation outline

// Accent hues derived from the palette for the three hierarchy levels + edit.
const BLUE_MED = "#3B82F6";
const BLUE_LIGHT = "#DBEAFE";
const BLUE_SUBTLE = "#F0F7FF";
const BORDER_MED = "#CBD5E1";
const LOCATION_COLOR = BLUE; // Location level → blue
const FLOOR_COLOR = "#0284C7"; // Floor level → info blue
const SHELF_COLOR = INK_MUTED; // Shelf level → slate
const AMBER = "#D97706"; // subtle amber — the "edit / modify" affordance
const AMBER_BG = "#FFFBEB";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Section header (INK label + subtle tinted level icon) ──────────────────
const SectionHeader = ({ icon, label, color }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1.5,
        bgcolor: `${color}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography fontWeight={700} fontSize={13} color={INK} letterSpacing={0.2}>
      {label}
    </Typography>
  </Box>
);

// ─── Compact text field (blue focus styling, red outline when invalid) ──────
const CField = ({
  label,
  value,
  onChange,
  select,
  children,
  error = false,
  helperText,
  ...rest
}) => (
  <TextField
    size="small"
    fullWidth
    label={label}
    value={value}
    onChange={onChange}
    select={select}
    error={!!error}
    helperText={helperText}
    sx={{
      "& .MuiInputBase-root": {
        fontSize: 13,
        borderRadius: 2,
        background: error ? "#FEF2F2" : SURFACE,
      },
      "& label": { fontSize: 13, color: error ? DANGER : INK_MUTED },
      "& label.Mui-focused": { color: error ? DANGER : BLUE_DARK },
      "& .MuiFormHelperText-root": { fontSize: 10.5, ml: 0.5, mt: 0.25 },
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: error ? DANGER : BORDER_MED,
          borderWidth: error ? "2px" : "1px",
        },
        "&:hover fieldset": { borderColor: error ? DANGER : BLUE_MED },
        "&.Mui-focused fieldset": {
          borderColor: error ? DANGER : BLUE,
          borderWidth: "2px",
        },
      },
    }}
    {...rest}
  >
    {children}
  </TextField>
);

// ─── Save / Cancel button row ───────────────────────────────────────────────
const ActionRow = ({ onSave, onCancel, editing }) => (
  <Box display="flex" gap={1}>
    <Button
      size="small"
      variant="contained"
      fullWidth
      disableElevation
      onClick={onSave}
      sx={{
        bgcolor: editing ? AMBER : BLUE,
        fontSize: 12,
        fontWeight: 700,
        py: 0.7,
        borderRadius: 2,
        textTransform: "none",
        boxShadow: "none",
        "&:hover": {
          bgcolor: editing ? "#B45309" : BLUE_DARK,
          boxShadow: "none",
        },
      }}
    >
      {editing ? "Update" : "Save"}
    </Button>
    {editing && (
      <Button
        size="small"
        variant="outlined"
        fullWidth
        onClick={onCancel}
        sx={{
          fontSize: 12,
          py: 0.7,
          borderRadius: 2,
          textTransform: "none",
          borderColor: BORDER_MED,
          color: INK_MUTED,
          "&:hover": { borderColor: INK_MUTED, background: BG_ALT },
        }}
      >
        Cancel
      </Button>
    )}
  </Box>
);

// ─── Small stat card for the summary strip ──────────────────────────────────
const StatCard = ({ icon, count, label, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.25,
      borderRadius: 3,
      border: `1px solid ${BORDER}`,
      bgcolor: SURFACE,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      transition: "box-shadow 0.18s, transform 0.18s",
      "&:hover": {
        boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
        transform: "translateY(-1px)",
      },
    }}
  >
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: 2,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `${color}15`,
        color,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography fontWeight={800} fontSize={18} color={INK} lineHeight={1}>
        {count}
      </Typography>
      <Typography fontSize={11} color={INK_MUTED} fontWeight={600} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

// ─── Dynamic MAC ID editor ──────────────────────────────────────────────────
const MacIdEditor = ({ macIds, onChange }) => {
  const [inputVal, setInputVal] = useState("");

  const add = () => {
    const trimmed = inputVal.trim();
    if (!trimmed || macIds.includes(trimmed)) return;
    onChange([...macIds, trimmed]);
    setInputVal("");
  };

  const remove = (idx) => onChange(macIds.filter((_, i) => i !== idx));

  const update = (idx, val) => {
    const updated = [...macIds];
    updated[idx] = val;
    onChange(updated);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
        <RouterIcon sx={{ fontSize: 13, color: SHELF_COLOR }} />
        <Typography fontSize={11} fontWeight={600} color={SHELF_COLOR}>
          MAC IDs
        </Typography>
        <Typography fontSize={10} color="text.disabled" ml={0.5}>
          ({macIds.length} added)
        </Typography>
      </Box>

      {/* Existing entries — inline editable */}
      {macIds.length > 0 && (
        <Box display="flex" flexDirection="column" gap={0.5} mb={0.75}>
          {macIds.map((mac, idx) => (
            <Box key={idx} display="flex" alignItems="center" gap={0.5}>
              <TextField
                size="small"
                value={mac}
                onChange={(e) => update(idx, e.target.value)}
                placeholder="AA:BB:CC:DD:EE:FF"
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    fontSize: 11,
                    fontFamily: "monospace",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: BORDER },
                    "&.Mui-focused fieldset": { borderColor: BLUE },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        fontSize={10}
                        color="text.disabled"
                        fontFamily="monospace"
                      >
                        {String(idx + 1).padStart(2, "0")}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                size="small"
                onClick={() => remove(idx)}
                sx={{
                  p: 0.4,
                  color: "#EF4444",
                  "&:hover": { background: "#FEF2F2" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Add new */}
      <Box display="flex" gap={0.5}>
        <TextField
          size="small"
          placeholder="Add MAC ID (e.g. AA:BB:CC:DD:EE:FF)"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { fontSize: 11, fontFamily: "monospace" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: BORDER_MED,
                borderStyle: "dashed",
              },
              "&.Mui-focused fieldset": {
                borderColor: BLUE,
                borderStyle: "solid",
              },
            },
          }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={add}
          sx={{
            minWidth: 36,
            px: 1,
            borderRadius: 2,
            borderColor: BLUE,
            color: BLUE,
            "&:hover": { background: BLUE_PALE, borderColor: BLUE_DARK },
          }}
        >
          <AddIcon sx={{ fontSize: 15 }} />
        </Button>
      </Box>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
const PawnFloorMaster = ({ embedded = false }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const { currentStoreInfo, userInfo } = useContext(AutoTaskAuthContext) || {};
  const loggedUser =
    JSON.parse(localStorage.getItem("CapacitorStorage.user") || "null") || {};
  const storeFromLS =
    JSON.parse(
      localStorage.getItem("CapacitorStorage.current_store_info") || "null"
    ) || {};
  const store_id =
    currentStoreInfo?.id ??
    storeFromLS?.id ??
    userInfo?.store_id ??
    loggedUser?.store_id;
  const user_id = userInfo?.id ?? loggedUser?.id;

  const [locations, setLocations] = useState([]);
  const [floors, setFloors] = useState([]);
  const [shelves, setShelves] = useState([]);

  // Tracked only to refresh the floor list of the location last worked on; the
  // form selects themselves are bound to the form payload values.
  const [selectedLocation, setSelectedLocation] = useState("");

  const [editingLocId, setEditingLocId] = useState(null);
  const [editingFloorId, setEditingFloorId] = useState(null);
  const [editingShelfId, setEditingShelfId] = useState(null);

  const [expandedLocs, setExpandedLocs] = useState({});
  const [expandedFloors, setExpandedFloors] = useState({});

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const showSnack = (msg, type = "success") =>
    setSnack({ open: true, message: msg, severity: type });

  // ─── Forms ─────────────────────────────────────────────────────────────────
  const [locForm, setLocForm] = useState({
    name: "",
    description: "",
    computer_name: "",
    printer_location: "",
    barcode_template: "",
  });

  const [floorForm, setFloorForm] = useState({
    name: "",
    floor_quick_no: "",
    location_id: "",
    tear_wt: 0,
    extra_tear: 0,
    tw_2: 0,
    tearnotes: "",
    cash_floor: false,
    shelf_wise: false,
    mac_ids: [],
  });

  const [shelfForm, setShelfForm] = useState({ shelf_name: "", floor_id: "" });

  // ─── Mandatory-field validation ──────────────────────────────────────────────
  // Pure validators — mirror the API's own required checks so a save never has
  // to round-trip just to be told a field is blank.
  const locErrorsOf = (f) => {
    const e = {};
    if (!f.name?.trim()) e.name = "Location name is required";
    return e;
  };
  const floorErrorsOf = (f) => {
    const e = {};
    if (!f.location_id) e.location_id = "Select a location";
    if (!f.name?.trim()) e.name = "Floor name is required";
    return e;
  };
  const shelfErrorsOf = (f) => {
    const e = {};
    if (!f.floor_id) e.floor_id = "Select a floor";
    if (!f.shelf_name?.trim()) e.shelf_name = "Shelf name is required";
    return e;
  };

  const [locErrors, setLocErrors] = useState({});
  const [floorErrors, setFloorErrors] = useState({});
  const [shelfErrors, setShelfErrors] = useState({});

  // Keep only the flags that are still invalid — returns `prev` untouched when
  // nothing changed so React bails out of the re-render.
  const pruneErrors = (prev, fresh) => {
    const keys = Object.keys(prev);
    if (!keys.length) return prev;
    const next = {};
    keys.forEach((k) => {
      if (fresh[k]) next[k] = fresh[k];
    });
    return Object.keys(next).length === keys.length ? prev : next;
  };

  // Once a form has been submitted with errors, drop each red outline the moment
  // the user fills that field in.
  useEffect(() => {
    setLocErrors((prev) => pruneErrors(prev, locErrorsOf(locForm)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locForm]);
  useEffect(() => {
    setFloorErrors((prev) => pruneErrors(prev, floorErrorsOf(floorForm)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorForm]);
  useEffect(() => {
    setShelfErrors((prev) => pruneErrors(prev, shelfErrorsOf(shelfForm)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelfForm]);

  // Flags the offending fields, warns, scrolls the form into view and blocks the
  // save. Returns true when the form is good to submit.
  const guard = (errs, setErrs, formId) => {
    setErrs(errs);
    if (!Object.keys(errs).length) return true;
    showSnack("Please fill all mandatory fields", "warning");
    document.getElementById(formId)?.scrollIntoView({ behavior: "smooth" });
    return false;
  };

  // ─── Resets ──────────────────────────────────────────────────────────────────
  const resetLoc = () => {
    setLocForm({
      name: "",
      description: "",
      computer_name: "",
      printer_location: "",
      barcode_template: "",
    });
    setLocErrors({});
    setEditingLocId(null);
  };
  const resetFloor = () => {
    setFloorForm({
      name: "",
      floor_quick_no: "",
      location_id: "",
      tear_wt: 0,
      extra_tear: 0,
      tw_2: 0,
      tearnotes: "",
      cash_floor: false,
      shelf_wise: false,
      mac_ids: [],
    });
    setFloorErrors({});
    setSelectedLocation("");
    setEditingFloorId(null);
  };
  const resetShelf = () => {
    setShelfForm({ shelf_name: "", floor_id: "" });
    setShelfErrors({});
    setEditingShelfId(null);
  };

  // ─── Fetch ───────────────────────────────────────────────────────────────────
  const fetchLocations = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/location/store/${store_id}`
      );
      setLocations(res.data.data || []);
    } catch {
      showSnack("Failed to fetch locations", "error");
    }
  };

  // Merges this location's floors into the list instead of replacing it —
  // replacing would wipe every other location's floors out of the tree.
  const fetchFloors = async (location_id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/jewel-master/floors`, {
        params: { location_id },
      });
      const fetched = res.data.data || [];
      setFloors((prev) =>
        [
          ...prev.filter((f) => String(f.location_id) !== String(location_id)),
          ...fetched,
        ].sort((a, b) => a.id - b.id)
      );
    } catch {
      showSnack("Failed to fetch floors", "error");
    }
  };

  const fetchAllFloors = async () => {
    if (store_id === undefined || store_id === null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/floors/store/${store_id}`
      );
      setFloors(res.data.data || []);
    } catch {}
  };

  const fetchShelves = async (floor_id) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/floors/${floor_id}`
      );
      setShelves((prev) => {
        const filtered = prev.filter(
          (s) => String(s.floor_id) !== String(floor_id)
        );
        return [
          ...filtered,
          ...(res.data.data?.shelves || []).map((s) => ({ ...s, floor_id })),
        ];
      });
    } catch {}
  };

  // Loaded upfront so the per-floor shelf-count chips and the Shelves stat are
  // right on first paint — without this they read 0 until the floor is expanded.
  const fetchAllShelves = async () => {
    if (store_id === undefined || store_id === null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/shelf/store/${store_id}`
      );
      setShelves(res.data.data || []);
    } catch {}
  };

  useEffect(() => {
    if (store_id === undefined || store_id === null) return;
    fetchLocations();
    fetchAllFloors();
    fetchAllShelves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store_id]);

  // ─── Save handlers ────────────────────────────────────────────────────────────
  const saveLocation = async () => {
    if (!guard(locErrorsOf(locForm), setLocErrors, "loc-form")) return;
    try {
      if (editingLocId) {
        await axios.put(
          `${API_BASE_URL}/api/jewel-master/location/${editingLocId}`,
          locForm
        );
        showSnack("Location updated");
      } else {
        await axios.post(`${API_BASE_URL}/api/jewel-master/location`, {
          ...locForm,
          store_id: store_id,
          created_by: user_id,
        });
        showSnack("Location created");
      }
      fetchLocations();
      resetLoc();
    } catch (e) {
      showSnack(
        apiErrorMessage(
          e,
          editingLocId ? "Failed to update location" : "Failed to create location",
        ),
        "error",
      );
    }
  };

  const saveFloor = async () => {
    if (!guard(floorErrorsOf(floorForm), setFloorErrors, "floor-form")) return;
    try {
      if (editingFloorId) {
        await axios.put(
          `${API_BASE_URL}/api/jewel-master/floors/${editingFloorId}`,
          floorForm
        );
        showSnack("Floor updated");
      } else {
        await axios.post(`${API_BASE_URL}/api/jewel-master/floors`, {
          ...floorForm,
          created_by: user_id,
        });
        showSnack("Floor created");
      }
      fetchAllFloors();
      if (selectedLocation) fetchFloors(selectedLocation);
      resetFloor();
    } catch (e) {
      showSnack(
        apiErrorMessage(
          e,
          editingFloorId ? "Failed to update floor" : "Failed to create floor",
        ),
        "error",
      );
    }
  };

  const saveShelf = async () => {
    if (!guard(shelfErrorsOf(shelfForm), setShelfErrors, "shelf-form")) return;
    try {
      if (editingShelfId) {
        await axios.put(
          `${API_BASE_URL}/api/jewel-master/shelf/${editingShelfId}`,
          shelfForm
        );
        showSnack("Shelf updated");
      } else {
        await axios.post(`${API_BASE_URL}/api/jewel-master/shelf`, {
          ...shelfForm,
          created_by: user_id,
        });
        showSnack("Shelf created");
      }
      fetchAllShelves();
      resetShelf();
    } catch (e) {
      showSnack(
        apiErrorMessage(
          e,
          editingShelfId ? "Failed to update shelf" : "Failed to create shelf",
        ),
        "error",
      );
    }
  };

  // ─── Click-to-edit ────────────────────────────────────────────────────────────
  const editLocation = (l) => {
    setLocForm({
      name: l.name,
      description: l.description || "",
      computer_name: l.computer_name || "",
      printer_location: l.printer_location || "",
      barcode_template: l.barcode_template || "",
    });
    setEditingLocId(l.id);
    document.getElementById("loc-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const editFloor = (f) => {
    setFloorForm({
      name: f.name,
      floor_quick_no: f.floor_quick_no || "",
      location_id: f.location_id || "",
      tear_wt: f.tear_wt || 0,
      extra_tear: f.extra_tear || 0,
      tw_2: f.tw_2 || 0,
      tearnotes: f.tearnotes || "",
      cash_floor: f.cash_floor || false,
      shelf_wise: f.shelf_wise || false,
      mac_ids: Array.isArray(f.mac_ids) ? f.mac_ids : [],
    });
    setEditingFloorId(f.id);
    setSelectedLocation(f.location_id || "");
    document
      .getElementById("floor-form")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const editShelf = (s) => {
    setShelfForm({ shelf_name: s.shelf_name, floor_id: s.floor_id || "" });
    setEditingShelfId(s.shelf_id);
    document
      .getElementById("shelf-form")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Quick-add helpers (prefill the form for a given parent) ────────────────────
  const quickAddFloor = (locId) => {
    resetFloor();
    setSelectedLocation(locId);
    fetchFloors(locId);
    setFloorForm((p) => ({ ...p, location_id: locId }));
    document
      .getElementById("floor-form")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const quickAddShelf = (floorId) => {
    resetShelf();
    fetchShelves(floorId);
    setShelfForm((p) => ({ ...p, floor_id: floorId }));
    document
      .getElementById("shelf-form")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Expand toggles ───────────────────────────────────────────────────────────
  const toggleLoc = (id) => {
    setExpandedLocs((p) => ({ ...p, [id]: !p[id] }));
    if (!floors.filter((f) => f.location_id === id).length) fetchFloors(id);
  };

  const toggleFloor = (id) => {
    setExpandedFloors((p) => ({ ...p, [id]: !p[id] }));
    fetchShelves(id);
  };

  const floorsForLoc = (locId) =>
    floors.filter((f) => String(f.location_id) === String(locId));
  const shelvesForFloor = (floorId) =>
    shelves.filter((s) => String(s.floor_id) === String(floorId));

  const totalDevices = floors.reduce(
    (n, f) => n + (Array.isArray(f.mac_ids) ? f.mac_ids.length : 0),
    0
  );

  // The tree fills whatever viewport is left below the header + stat strip and
  // scrolls inside itself, so everything can be expanded without the page (or
  // the card) growing past the fold. Natural height on phones, where the list
  // sits under the forms in a single column.
  const HIERARCHY_H = embedded ? "calc(100vh - 200px)" : "calc(100vh - 190px)";

  // ════════════════════════════════════════════════════════════════════════════
  return (
    <Box
      sx={
        embedded
          ? { p: 0, minWidth: 0 }
          : {
              height: "100%",
              minWidth: 0,
              overflowY: "auto",
              overflowX: "hidden",
              bgcolor: BG_ALT,
              p: { xs: 1.25, sm: 1.5, md: 2.5 },
            }
      }
    >
      {/* ── Header (hidden when embedded in the unified Masters screen) ── */}
      {!embedded && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            <WarehouseRoundedIcon />
          </Box>
          <Box minWidth={0}>
            <Typography
              sx={{ fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK }}
            >
              Floor Master
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }} noWrap>
              {currentStoreInfo?.name
                ? `${currentStoreInfo.name}${
                    currentStoreInfo?.branch
                      ? ` · ${currentStoreInfo.branch}`
                      : ""
                  }`
                : "Manage locations, floors & shelves"}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Summary stats ── */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<PinDropRoundedIcon sx={{ fontSize: 20 }} />}
            count={locations.length}
            label="Locations"
            color={LOCATION_COLOR}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<LayersIcon sx={{ fontSize: 20 }} />}
            count={floors.length}
            label="Floors"
            color={FLOOR_COLOR}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<ViewModuleIcon sx={{ fontSize: 20 }} />}
            count={shelves.length}
            label="Shelves"
            color={SHELF_COLOR}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<RouterIcon sx={{ fontSize: 20 }} />}
            count={totalDevices}
            label="Devices (MAC)"
            color={INK_SOFT}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="flex-start">
        {/* ── LEFT — stacked forms ── */}
        <Grid item md={6} sm={6} xs={12}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            {/* Location form */}
            <Card
              id="loc-form"
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
              }}
            >
              <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                <Grid container spacing={1}>
                  <Grid
                    item
                    md={editingLocId ? 8 : 10}
                    sm={8}
                    xs={editingLocId ? 6 : 8}
                  >
                    <SectionHeader
                      icon={<PinDropRoundedIcon sx={{ fontSize: 15 }} />}
                      label={editingLocId ? "Edit Location" : "New Location"}
                      color={LOCATION_COLOR}
                    />
                  </Grid>
                  <Grid
                    item
                    md={editingLocId ? 4 : 2}
                    sm={4}
                    xs={editingLocId ? 6 : 4}
                    justifySelf={"flex-end"}
                  >
                    <ActionRow
                      onSave={saveLocation}
                      onCancel={resetLoc}
                      editing={!!editingLocId}
                    />
                  </Grid>
                </Grid>

                <Box display="flex" flexDirection="column" gap={1}>
                  <Grid container spacing={1}>
                    <Grid item md={2.4} sm={6} xs={12}>
                      <CField
                        label="Name *"
                        value={locForm.name}
                        onChange={(e) =>
                          setLocForm({ ...locForm, name: e.target.value })
                        }
                        error={!!locErrors.name}
                        helperText={locErrors.name}
                      />
                    </Grid>
                    <Grid item md={2.4} sm={6} xs={12}>
                      <CField
                        label="Description"
                        value={locForm.description}
                        onChange={(e) =>
                          setLocForm({
                            ...locForm,
                            description: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item md={2.4} sm={6} xs={12}>
                      <CField
                        label="Computer Name"
                        value={locForm.computer_name}
                        onChange={(e) =>
                          setLocForm({
                            ...locForm,
                            computer_name: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item md={2.4} sm={6} xs={12}>
                      <CField
                        label="Printer Share"
                        value={locForm.printer_location}
                        onChange={(e) =>
                          setLocForm({
                            ...locForm,
                            printer_location: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item md={2.4} sm={6} xs={12}>
                      <CField
                        label="Barcode Template"
                        value={locForm.barcode_template}
                        onChange={(e) =>
                          setLocForm({
                            ...locForm,
                            barcode_template: e.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>

            {/* Floor form */}
            <Card
              id="floor-form"
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
              }}
            >
              <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                <Grid container spacing={1}>
                  <Grid
                    item
                    md={editingFloorId ? 8 : 10}
                    sm={8}
                    xs={editingFloorId ? 6 : 8}
                  >
                    <SectionHeader
                      icon={<LayersIcon sx={{ fontSize: 15 }} />}
                      label={editingFloorId ? "Edit Floor" : "New Floor"}
                      color={FLOOR_COLOR}
                    />
                  </Grid>
                  <Grid
                    item
                    md={editingFloorId ? 4 : 2}
                    sm={4}
                    xs={editingFloorId ? 6 : 4}
                    justifySelf={"flex-end"}
                  >
                    <ActionRow
                      onSave={saveFloor}
                      onCancel={resetFloor}
                      editing={!!editingFloorId}
                    />
                  </Grid>
                </Grid>

                <Box display="flex" flexDirection="column" gap={1}>
                  <Grid container spacing={1}>
                    <Grid item md={2} xs={12} sm={8}>
                      <CField
                        select
                        label="Location *"
                        value={floorForm.location_id}
                        onChange={(e) => {
                          setSelectedLocation(e.target.value);
                          fetchFloors(e.target.value);
                          setFloorForm({
                            ...floorForm,
                            location_id: e.target.value,
                          });
                        }}
                        error={!!floorErrors.location_id}
                        helperText={floorErrors.location_id}
                      >
                        {locations.map((l) => (
                          <MenuItem
                            key={l.id}
                            value={l.id}
                            sx={{ fontSize: 13 }}
                          >
                            {l.name}
                          </MenuItem>
                        ))}
                      </CField>
                    </Grid>
                    <Grid item md={2.75} xs={12} sm={8}>
                      <CField
                        label="Floor Name *"
                        value={floorForm.name}
                        onChange={(e) =>
                          setFloorForm({ ...floorForm, name: e.target.value })
                        }
                        error={!!floorErrors.name}
                        helperText={floorErrors.name}
                      />
                    </Grid>
                    <Grid item md={2} xs={12} sm={4}>
                      <CField
                        label="Quick No"
                        value={floorForm.floor_quick_no}
                        onChange={(e) =>
                          setFloorForm({
                            ...floorForm,
                            floor_quick_no: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item md={1.75} xs={4}>
                      <CField
                        label="Tear WT"
                        value={floorForm.tear_wt}
                        onChange={(e) =>
                          setFloorForm({
                            ...floorForm,
                            tear_wt: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item md={1.75} xs={4}>
                      <CField
                        label="Extra Tear"
                        value={floorForm.extra_tear}
                        onChange={(e) =>
                          setFloorForm({
                            ...floorForm,
                            extra_tear: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item md={1.75} xs={4}>
                      <CField
                        label="TW2"
                        value={floorForm.tw_2}
                        onChange={(e) =>
                          setFloorForm({ ...floorForm, tw_2: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item md={4.75} xs={4}>
                      <CField
                        label="Contents / Notes"
                        value={floorForm.tearnotes}
                        onChange={(e) =>
                          setFloorForm({
                            ...floorForm,
                            tearnotes: e.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid>

                  {/* Cash Floor & Shelf-wise Checkboxes */}
                  <Box display="flex" gap={1}>
                    <FormControlLabel
                      sx={{
                        "& .MuiFormControlLabel-label": { fontSize: 12 },
                        m: 0,
                      }}
                      control={
                        <Checkbox
                          size="small"
                          sx={{
                            color: BORDER_MED,
                            "&.Mui-checked": { color: BLUE },
                          }}
                          checked={floorForm.cash_floor}
                          onChange={(e) =>
                            setFloorForm({
                              ...floorForm,
                              cash_floor: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Cash Floor"
                    />
                    <FormControlLabel
                      sx={{
                        "& .MuiFormControlLabel-label": { fontSize: 12 },
                        m: 0,
                      }}
                      control={
                        <Checkbox
                          size="small"
                          sx={{
                            color: BORDER_MED,
                            "&.Mui-checked": { color: BLUE },
                          }}
                          checked={floorForm.shelf_wise}
                          onChange={(e) =>
                            setFloorForm({
                              ...floorForm,
                              shelf_wise: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Shelf Wise"
                    />
                  </Box>

                  {/* Dynamic MAC ID editor */}
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: `1px solid ${BORDER}`,
                      background: BLUE_SUBTLE,
                    }}
                  >
                    <MacIdEditor
                      macIds={floorForm.mac_ids}
                      onChange={(updated) =>
                        setFloorForm({ ...floorForm, mac_ids: updated })
                      }
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Shelf form */}
            <Card
              id="shelf-form"
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
              }}
            >
              <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                <Grid container spacing={1}>
                  <Grid
                    item
                    md={editingShelfId ? 8 : 10}
                    sm={8}
                    xs={editingShelfId ? 6 : 8}
                  >
                    <SectionHeader
                      icon={<ViewModuleIcon sx={{ fontSize: 15 }} />}
                      label={editingShelfId ? "Edit Shelf" : "New Shelf"}
                      color={SHELF_COLOR}
                    />
                  </Grid>
                  <Grid
                    item
                    md={editingShelfId ? 4 : 2}
                    sm={4}
                    xs={editingShelfId ? 6 : 4}
                    justifySelf={"flex-end"}
                  >
                    <ActionRow
                      onSave={saveShelf}
                      onCancel={resetShelf}
                      editing={!!editingShelfId}
                    />
                  </Grid>
                </Grid>

                <Box display="flex" flexDirection="column" gap={1}>
                  <Grid container spacing={1}>
                    <Grid item md={6} sm={4} xs={5}>
                      <CField
                        select
                        label="Floor *"
                        value={shelfForm.floor_id}
                        onChange={(e) => {
                          fetchShelves(e.target.value);
                          setShelfForm({
                            ...shelfForm,
                            floor_id: e.target.value,
                          });
                        }}
                        error={!!shelfErrors.floor_id}
                        helperText={shelfErrors.floor_id}
                      >
                        {floors.map((f) => (
                          <MenuItem
                            key={f.id}
                            value={f.id}
                            sx={{ fontSize: 13 }}
                          >
                            {f.name}
                          </MenuItem>
                        ))}
                      </CField>
                    </Grid>
                    <Grid item md={6} sm={4} xs={7}>
                      <CField
                        label="Shelf Name *"
                        value={shelfForm.shelf_name}
                        onChange={(e) =>
                          setShelfForm({
                            ...shelfForm,
                            shelf_name: e.target.value,
                          })
                        }
                        error={!!shelfErrors.shelf_name}
                        helperText={shelfErrors.shelf_name}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* ── RIGHT — unified hierarchical list ── */}
        <Grid item md={6} sm={6} xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${BORDER}`,
              bgcolor: SURFACE,
              display: "flex",
              flexDirection: "column",
              minHeight: 420,
              height: { xs: "auto", sm: HIERARCHY_H },
            }}
          >
            <CardContent
              sx={{
                p: 1.75,
                "&:last-child": { pb: 1.75 },
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                mb={1.5}
                flexShrink={0}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AccountTreeRoundedIcon sx={{ fontSize: 18, color: BLUE }} />
                  <Typography fontWeight={700} fontSize={14} color={INK}>
                    Hierarchy
                  </Typography>
                </Box>
                {/* Legend */}
                <Box display="flex" alignItems="center" gap={1.25}>
                  {[
                    { c: LOCATION_COLOR, t: "Location" },
                    { c: FLOOR_COLOR, t: "Floor" },
                    { c: SHELF_COLOR, t: "Shelf" },
                  ].map((lg) => (
                    <Box
                      key={lg.t}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <Box
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: lg.c,
                        }}
                      />
                      <Typography fontSize={10.5} color={INK_MUTED}>
                        {lg.t}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Scroll region — the tree grows here, not on the page. The
                  scrollbar is hidden on every engine (wheel/touch still work). */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {locations.length === 0 && (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      px: 2,
                      borderRadius: 2.5,
                      border: `1px dashed ${BORDER_MED}`,
                      background: BLUE_SUBTLE,
                    }}
                  >
                    <PinDropRoundedIcon
                      sx={{ fontSize: 40, color: BORDER_MED, mb: 1 }}
                    />
                    <Typography fontSize={13} fontWeight={600} color={INK_SOFT}>
                      No locations yet
                    </Typography>
                    <Typography fontSize={11.5} color={INK_MUTED}>
                      Add your first location from the form on the left.
                    </Typography>
                  </Box>
                )}

                {locations.map((loc) => {
                  const locFloors = floorsForLoc(loc.id);
                  const isLocExpanded = expandedLocs[loc.id];

                  return (
                    <Box key={loc.id} mb={1}>
                      {/* Location row */}
                      <Box
                        onClick={() => toggleLoc(loc.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1.5,
                          py: 1.1,
                          borderRadius: 2,
                          border: `1px solid ${
                            isLocExpanded ? BLUE_LIGHT : BORDER
                          }`,
                          borderLeft: `3px solid ${LOCATION_COLOR}`,
                          background: isLocExpanded ? BLUE_PALE : SURFACE,
                          cursor: "pointer",
                          "&:hover": { background: BLUE_PALE },
                          transition: "background 0.15s, border-color 0.15s",
                        }}
                      >
                        <IconButton size="small" sx={{ mr: 0.5, p: 0.3 }}>
                          {isLocExpanded ? (
                            <ExpandLessIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: 1.5,
                            background: LOCATION_COLOR,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1,
                            flexShrink: 0,
                            boxShadow: `0 2px 6px ${LOCATION_COLOR}44`,
                          }}
                        >
                          <StorefrontIcon
                            sx={{ fontSize: 15, color: "#fff" }}
                          />
                        </Box>
                        <Box flex={1} minWidth={0}>
                          <Typography
                            fontWeight={700}
                            fontSize={13}
                            color={INK}
                            noWrap
                          >
                            {loc.name}
                          </Typography>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                          >
                            {loc.description && (
                              <Typography
                                fontSize={11}
                                color="text.secondary"
                                noWrap
                              >
                                {loc.description}
                              </Typography>
                            )}
                            {loc.computer_name && (
                              <Typography fontSize={10.5} color={INK_MUTED}>
                                🖥 {loc.computer_name}
                              </Typography>
                            )}
                            {loc.printer_location && (
                              <Typography fontSize={10.5} color={INK_MUTED}>
                                🖨 {loc.printer_location}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Chip
                          label={`${locFloors.length} floor${
                            locFloors.length !== 1 ? "s" : ""
                          }`}
                          size="small"
                          sx={{
                            fontSize: 10,
                            height: 20,
                            background: BLUE_LIGHT,
                            color: BLUE_DARK,
                            fontWeight: 700,
                            mr: 0.5,
                          }}
                        />
                        <Tooltip title="Add floor here">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              quickAddFloor(loc.id);
                            }}
                            sx={{
                              p: 0.4,
                              color: BLUE,
                              "&:hover": { background: BLUE_PALE },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit location">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              editLocation(loc);
                            }}
                            sx={{
                              p: 0.4,
                              color: AMBER,
                              "&:hover": { background: AMBER_BG },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Floors collapse */}
                      <Collapse in={isLocExpanded}>
                        <Box
                          ml={isMobile ? 1.5 : 3.5}
                          mt={0.5}
                          display="flex"
                          flexDirection="column"
                          gap={0.5}
                          sx={{
                            borderLeft: `1.5px dashed ${BORDER_MED}`,
                            pl: 1.25,
                          }}
                        >
                          {locFloors.length === 0 && (
                            <Typography
                              fontSize={11}
                              color="text.disabled"
                              pl={1}
                              py={0.5}
                            >
                              No floors for this location.
                            </Typography>
                          )}

                          {locFloors.map((floor) => {
                            const floorShelves = shelvesForFloor(floor.id);
                            const isFloorExpanded = expandedFloors[floor.id];
                            const macCount = Array.isArray(floor.mac_ids)
                              ? floor.mac_ids.length
                              : 0;

                            return (
                              <Box key={floor.id}>
                                {/* Floor row */}
                                <Box
                                  onClick={() => toggleFloor(floor.id)}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    px: 1.5,
                                    py: 0.9,
                                    borderRadius: 2,
                                    border: `1px solid ${
                                      isFloorExpanded ? "#BAE6FD" : BORDER
                                    }`,
                                    borderLeft: `3px solid ${FLOOR_COLOR}`,
                                    background: isFloorExpanded
                                      ? "#F0F9FF"
                                      : "#FAFBFC",
                                    cursor: "pointer",
                                    "&:hover": { background: BLUE_PALE },
                                    transition: "background 0.15s",
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{ mr: 0.5, p: 0.3 }}
                                  >
                                    {isFloorExpanded ? (
                                      <ExpandLessIcon sx={{ fontSize: 15 }} />
                                    ) : (
                                      <ExpandMoreIcon sx={{ fontSize: 15 }} />
                                    )}
                                  </IconButton>
                                  <Box
                                    sx={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: 1.2,
                                      background: FLOOR_COLOR,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      mr: 1,
                                      flexShrink: 0,
                                    }}
                                  >
                                    <LayersIcon
                                      sx={{ fontSize: 13, color: "#fff" }}
                                    />
                                  </Box>
                                  <Box flex={1} minWidth={0}>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.75}
                                      flexWrap="wrap"
                                    >
                                      <Typography
                                        fontWeight={600}
                                        fontSize={12.5}
                                        color={INK}
                                        noWrap
                                      >
                                        {floor.name}
                                      </Typography>
                                      {floor.cash_floor && (
                                        <Chip
                                          label="Cash"
                                          size="small"
                                          sx={{
                                            height: 16,
                                            fontSize: 9.5,
                                            fontWeight: 700,
                                            background: "#ECFDF5",
                                            color: SUCCESS,
                                          }}
                                        />
                                      )}
                                      {floor.shelf_wise && (
                                        <Chip
                                          label="Shelf-wise"
                                          size="small"
                                          sx={{
                                            height: 16,
                                            fontSize: 9.5,
                                            fontWeight: 700,
                                            background: BLUE_LIGHT,
                                            color: BLUE_DARK,
                                          }}
                                        />
                                      )}
                                    </Box>
                                    <Typography
                                      fontSize={10.5}
                                      color="text.secondary"
                                      noWrap
                                    >
                                      Q:{floor.floor_quick_no || "—"}{" "}
                                      &nbsp;·&nbsp; TW:{floor.tear_wt}
                                      {macCount > 0 && (
                                        <span>
                                          {" "}
                                          &nbsp;·&nbsp;{" "}
                                          <RouterIcon
                                            sx={{
                                              fontSize: 10,
                                              verticalAlign: "middle",
                                            }}
                                          />{" "}
                                          {macCount} MAC
                                          {macCount !== 1 ? "s" : ""}
                                        </span>
                                      )}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={`${floorShelves.length} shel${
                                      floorShelves.length !== 1 ? "ves" : "f"
                                    }`}
                                    size="small"
                                    sx={{
                                      fontSize: 10,
                                      height: 18,
                                      background: "#E0F2FE",
                                      color: "#0369A1",
                                      fontWeight: 700,
                                      mr: 0.5,
                                    }}
                                  />
                                  <Tooltip title="Add shelf here">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        quickAddShelf(floor.id);
                                      }}
                                      sx={{
                                        p: 0.4,
                                        color: FLOOR_COLOR,
                                        "&:hover": { background: "#F0F9FF" },
                                      }}
                                    >
                                      <AddIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit floor">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        editFloor(floor);
                                      }}
                                      sx={{
                                        p: 0.4,
                                        color: AMBER,
                                        "&:hover": { background: AMBER_BG },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>

                                {/* Shelves + MACs expand */}
                                <Collapse in={isFloorExpanded}>
                                  <Box
                                    ml={isMobile ? 1.5 : 3.5}
                                    mt={0.5}
                                    display="flex"
                                    flexDirection="column"
                                    gap={0.4}
                                    sx={{
                                      borderLeft: `1.5px dashed ${BORDER}`,
                                      pl: 1.25,
                                    }}
                                  >
                                    {/* MAC ID chips */}
                                    {macCount > 0 && (
                                      <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        alignItems="center"
                                        gap={0.5}
                                        px={1}
                                        py={0.5}
                                        sx={{
                                          background: BLUE_SUBTLE,
                                          borderRadius: 1.5,
                                          border: `1px dashed ${BLUE_LIGHT}`,
                                        }}
                                      >
                                        <RouterIcon
                                          sx={{
                                            fontSize: 12,
                                            color: SHELF_COLOR,
                                          }}
                                        />
                                        {floor.mac_ids.map((mac, i) => (
                                          <Chip
                                            key={i}
                                            label={mac}
                                            size="small"
                                            sx={{
                                              fontSize: 10,
                                              height: 18,
                                              fontFamily: "monospace",
                                              background: "#fff",
                                              border: `1px solid ${BLUE_LIGHT}`,
                                              color: INK_SOFT,
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    )}

                                    {/* Shelves */}
                                    {floorShelves.length === 0 && (
                                      <Typography
                                        fontSize={11}
                                        color="text.disabled"
                                        pl={1}
                                        py={0.3}
                                      >
                                        No shelves on this floor.
                                      </Typography>
                                    )}
                                    {floorShelves.map((shelf) => (
                                      <Box
                                        key={shelf.shelf_id}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          px: 1.5,
                                          py: 0.6,
                                          borderRadius: 1.5,
                                          border: `1px solid ${BORDER}`,
                                          borderLeft: `3px solid ${SHELF_COLOR}`,
                                          background: "#fff",
                                          "&:hover": { background: BG_ALT },
                                          transition: "background 0.15s",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: 0.8,
                                            background: SHELF_COLOR,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 1,
                                            flexShrink: 0,
                                          }}
                                        >
                                          <ViewModuleIcon
                                            sx={{ fontSize: 11, color: "#fff" }}
                                          />
                                        </Box>
                                        <Typography
                                          flex={1}
                                          fontSize={11.5}
                                          fontWeight={500}
                                          color={INK_SOFT}
                                          noWrap
                                        >
                                          {shelf.shelf_name}
                                        </Typography>
                                        <Tooltip title="Edit shelf">
                                          <IconButton
                                            size="small"
                                            onClick={() => editShelf(shelf)}
                                            sx={{
                                              p: 0.3,
                                              color: AMBER,
                                              "&:hover": {
                                                background: AMBER_BG,
                                              },
                                            }}
                                          >
                                            <EditIcon sx={{ fontSize: 12 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    ))}
                                  </Box>
                                </Collapse>
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          sx={{ fontWeight: 500, fontSize: 12 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnFloorMaster;
